import { useOutletContext } from "@remix-run/react"
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react"
import { calculateBalances } from "~/calculations/portfolioCalculations/calculateBalances"
import { initialFormData } from "~/components/homepage/initialFormData"
import type { ContextProps } from "~/types/global.types"
import { Forms } from "~/utils/products"

type FilterForms = Record<string, boolean>

const EMPTY_TOTALS = {
  assets: 0,
  debts: 0,
  all() {
    return 0
  },
}

const EMPTY_LIQUIDITY = { liquid: 0, nonLiquid: 0 }
const EMPTY_BALANCES: Record<number, number> = {}

function readFilterForms(): FilterForms {
  const fallback = { ...initialFormData.category }
  if (typeof window === "undefined") return fallback
  const raw = window.localStorage.getItem("filterState2")
  if (!raw) return fallback
  try {
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return fallback
    return { ...fallback, ...parsed }
  } catch {
    return fallback
  }
}

function sameForms(a: FilterForms, b: FilterForms) {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)])
  for (const key of keys) {
    if (Boolean(a[key]) !== Boolean(b[key])) return false
  }
  return true
}

function useDashboardDataCompute(enabled: boolean) {
  const { products, fiat, crypto, stocks, marketStats } = useOutletContext<ContextProps>()

  // Start from the defaults so the server render and the first client render
  // match. Saved filters are applied after mount.
  const [forms, setForms] = useState<FilterForms>({ ...initialFormData.category })

  useEffect(() => {
    if (!enabled) return
    const apply = () => {
      const next = readFilterForms()
      setForms((prev) => (sameForms(prev, next) ? prev : next))
    }
    apply()
    document.addEventListener("filterState2Updated", apply)
    return () => document.removeEventListener("filterState2Updated", apply)
  }, [enabled])

  const filteredProducts = useMemo(() => {
    if (!enabled) return products
    return products.filter((product) => {
      if (product.form === Forms.BANK && forms[Forms.BANK]) return true
      if (product.form === Forms.REALESTATE && forms[Forms.REALESTATE]) return true
      if (product.form === Forms.PENSION && forms[Forms.PENSION]) return true
      if (product.form === Forms.CRYPTO && forms[Forms.CRYPTO]) return true
      if (product.form === Forms.SHARE && forms[Forms.SHARE]) return true
      if (product.form === Forms.DEBT && forms[Forms.DEBT]) return true
      if (product.form === Forms.OTHER && forms[Forms.OTHER]) return true
      if (product.form === Forms.RSU && forms[Forms.RSU]) return true
      return false
    })
  }, [enabled, products, forms])

  const { totalBalances } = useMemo(() => {
    if (!enabled) return { totalBalances: EMPTY_TOTALS }
    return calculateBalances(filteredProducts, fiat, crypto, stocks, {
      allForms: true,
    })
  }, [enabled, filteredProducts, fiat, crypto, stocks])

  const liquidityDistrubtion = useMemo(() => {
    if (!enabled) return EMPTY_LIQUIDITY
    const { totalBalances } = calculateBalances(
      filteredProducts,
      fiat,
      crypto,
      stocks,
      {
        allForms: true,
        liquid: true,
      }
    )
    const liquid: number = totalBalances.liquidAssets
      ? totalBalances.liquidAssets
      : 0
    const nonLiquid = totalBalances.nonLiquidAssets
    return { liquid, nonLiquid }
  }, [enabled, filteredProducts, fiat, crypto, stocks])

  // Full portfolio, ignoring category filters. The assets map and currency
  // chart both need this; computing it here means they share one pass.
  const { balancesInBase } = useMemo(() => {
    if (!enabled) return { balancesInBase: EMPTY_BALANCES }
    return calculateBalances(products, fiat, crypto, stocks, {
      allForms: true,
    })
  }, [enabled, products, fiat, crypto, stocks])

  return useMemo(
    () => ({
      products,
      totalBalances,
      liquidityDistrubtion,
      marketStats,
      fiat,
      crypto,
      stocks,
      balancesInBase,
    }),
    [products, totalBalances, liquidityDistrubtion, marketStats, fiat, crypto, stocks, balancesInBase]
  )
}

const DashboardDataContext = createContext<ReturnType<typeof useDashboardDataCompute> | null>(null)

export function DashboardDataProvider({ children }: { children: ReactNode }) {
  const value = useDashboardDataCompute(true)
  return (
    <DashboardDataContext.Provider value={value}>
      {children}
    </DashboardDataContext.Provider>
  )
}

function useDashboardData() {
  const shared = useContext(DashboardDataContext)
  const local = useDashboardDataCompute(shared == null)
  return shared ?? local
}

export default useDashboardData
